import { useState, useEffect } from 'react';

interface InstallAppButtonProps {
  label?: string;
  variant?: 'inline' | 'floating';
}

type Device = 'ios' | 'android' | 'other';

export default function InstallAppButton({
  label = 'Installer sur mon téléphone',
  variant = 'inline',
}: InstallAppButtonProps) {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState<Device>('other');
  const [dismissed, setDismissed] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return window.localStorage.getItem('cotidel_install_bubble_dismissed') === '1';
  });

  const dismiss = () => {
    setDismissed(true);
    try {
      window.localStorage.setItem('cotidel_install_bubble_dismissed', '1');
    } catch {
      /* stockage indisponible */
    }
  };

  useEffect(() => {
    const ua = navigator.userAgent;
    if (/iPhone|iPad|iPod/i.test(ua)) setTab('ios');
    else if (/Android/i.test(ua)) setTab('android');
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  const buttonBase =
    'flex items-center justify-center gap-2 cursor-pointer whitespace-nowrap transition-all duration-200';

  return (
    <>
      {variant === 'floating' ? (
        !dismissed && (
          <div className="fixed right-3 top-1/2 -translate-y-1/2 z-40 md:hidden flex flex-col items-end gap-1.5">
            <button
              onClick={dismiss}
              aria-label="Masquer le bouton d'installation"
              className="w-7 h-7 rounded-full bg-background-100 border border-background-200 text-foreground-400 hover:text-foreground-700 hover:bg-background-200 flex items-center justify-center cursor-pointer"
            >
              <i className="ri-close-line text-sm"></i>
            </button>
            <button
              onClick={() => setOpen(true)}
              className={`${buttonBase} px-4 py-3 rounded-full bg-primary-500 text-white hover:bg-primary-600`}
            >
              <i className="ri-smartphone-line text-lg"></i>
              <span className="text-sm font-semibold whitespace-nowrap">{label}</span>
            </button>
          </div>
        )
      ) : (
        <button
          onClick={() => setOpen(true)}
          className={`${buttonBase} w-full px-4 py-3 rounded-md bg-accent-50 border border-accent-200 text-accent-800 hover:bg-accent-100 text-sm font-semibold`}
        >
          <i className="ri-smartphone-line text-base"></i>
          {label}
        </button>
      )}

      {open && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
          <div
            className="absolute inset-0 bg-foreground-950/40 backdrop-blur-sm"
            onClick={() => setOpen(false)}
          ></div>

          <div className="relative w-full max-w-md bg-background-50 rounded-t-2xl sm:rounded-2xl p-6 animate-fadeIn max-h-[90vh] overflow-y-auto">
            {/* En-tête */}
            <div className="flex items-start justify-between mb-5">
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-xl bg-primary-500 flex items-center justify-center flex-shrink-0">
                  <i className="ri-shield-check-line text-white text-xl"></i>
                </div>
                <div>
                  <h3 className="text-lg font-bold font-heading text-foreground-950 leading-tight">
                    Installer CotIdel
                  </h3>
                  <p className="text-xs text-foreground-500 mt-0.5">
                    Sur l&apos;écran d&apos;accueil de votre téléphone
                  </p>
                </div>
              </div>
              <button
                onClick={() => setOpen(false)}
                className="w-8 h-8 flex items-center justify-center rounded-full text-foreground-400 hover:text-foreground-700 hover:bg-background-100 cursor-pointer flex-shrink-0"
                aria-label="Fermer"
              >
                <i className="ri-close-line text-lg"></i>
              </button>
            </div>

            {/* Sélecteur iPhone / Android */}
            <div className="flex gap-1 p-1 bg-background-100 rounded-full mb-6">
              <button
                onClick={() => setTab('ios')}
                className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-full text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${
                  tab === 'ios'
                    ? 'bg-white text-foreground-950'
                    : 'text-foreground-500 hover:text-foreground-700'
                }`}
              >
                <i className="ri-apple-line"></i>
                iPhone
              </button>
              <button
                onClick={() => setTab('android')}
                className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-full text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${
                  tab === 'android'
                    ? 'bg-white text-foreground-950'
                    : 'text-foreground-500 hover:text-foreground-700'
                }`}
              >
                <i className="ri-android-line"></i>
                Android
              </button>
            </div>

            {/* Étapes */}
            {tab === 'ios' && <IosSteps />}
            {tab === 'android' && <AndroidSteps />}

            {/* Note */}
            <div className="mt-6 flex items-start gap-2.5 p-3 rounded-lg bg-background-100 border border-background-200/60">
              <i className="ri-information-line text-foreground-400 mt-0.5"></i>
              <p className="text-xs text-foreground-500 leading-relaxed">
                C&apos;est un raccourci qui ouvre CotIdel en plein écran, avec son icône et son nom,
                exactement comme une application. Aucun téléchargement depuis un store.
              </p>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function Step({ num, title, detail }: { num: number; title: string; detail: string }) {
  return (
    <div className="flex gap-3">
      <div className="w-7 h-7 rounded-full bg-primary-500 text-white text-sm font-bold flex items-center justify-center flex-shrink-0 mt-0.5">
        {num}
      </div>
      <div>
        <p className="text-sm font-semibold text-foreground-900">{title}</p>
        <p className="text-xs text-foreground-500 mt-0.5 leading-relaxed">{detail}</p>
      </div>
    </div>
  );
}

function IosSteps() {
  return (
    <div className="space-y-4">
      <Step
        num={1}
        title="Ouvrez CotIdel dans Safari"
        detail="L&apos;application doit être ouverte avec Safari (pas Chrome ni une autre app)."
      />
      <Step
        num={2}
        title="Touchez le bouton Partager"
        detail="Le carré avec une flèche vers le haut, en bas de l&apos;écran."
      />
      <Step
        num={3}
        title="Choisissez « Sur l&apos;écran d&apos;accueil »"
        detail="Faites défiler le menu si besoin."
      />
      <Step
        num={4}
        title="Touchez « Ajouter »"
        detail="L&apos;icône CotIdel apparaît sur votre écran d&apos;accueil."
      />
    </div>
  );
}

function AndroidSteps() {
  return (
    <div className="space-y-4">
      <Step
        num={1}
        title="Ouvrez CotIdel dans Chrome"
        detail="L&apos;application doit être ouverte avec Chrome (pas une autre app)."
      />
      <Step
        num={2}
        title="Touchez le menu ⋮"
        detail="Les trois points verticaux, en haut à droite de l&apos;écran."
      />
      <Step
        num={3}
        title="Choisissez « Ajouter à l&apos;écran d&apos;accueil »"
        detail="Ou « Installer l&apos;application » selon votre version."
      />
      <Step
        num={4}
        title="Touchez « Ajouter »"
        detail="L&apos;icône CotIdel apparaît sur votre écran d&apos;accueil."
      />
    </div>
  );
}